/**
 * The Settings navigation model — one table, three groups, six destinations.
 *
 * Settings used to be three tabs where "General" carried NINE cards: an
 * Administration card wrapping a single link, the whole tenancy-admin surface,
 * twenty tenancy-default selects, the REST/GraphQL transport, a theme picker, an
 * inspector switch, and — at the bottom — an Identity card and a wall of ~330
 * scope badges that between them re-rendered `/profile`. Everything a person can
 * change about themselves sat below everything an org admin changes once a year.
 *
 * So the cards are split onto their own routes and grouped by WHOSE setting it
 * is, which is the only division that predicts who is looking for it:
 *
 *  - **You** — settles in seconds, changed by anyone, affects only you.
 *  - **Workspace** — changed by an admin, affects everybody in the tenant.
 *  - **Advanced** — a real setting with a small audience; parked rather than
 *    hidden, because the `?debug=1` panel is already where the diagnostics live
 *    and this is not one.
 *
 * Every entry is a real sub-route, so each is deep-linkable and preloads on
 * intent — which is what lets a scope pill link at the exact card it names
 * (`/settings/defaults#scope-default-visibility`) rather than at the top of a
 * page and a scroll.
 *
 * Guarded by `settingsNav.test.ts`: an entry with no route, a settings route
 * with no entry, and a group that is not in the table all fail there. The rail
 * has the same guard (`config/navigation.test.ts`) for the same reason — a
 * config surface nobody can find is indistinguishable from one nobody built.
 */
import { ENTITLEMENT } from "@authe/fe-commons/plans";
import { RESOURCES, ROUTES } from "@/config";

/** The groups, in the order the nav renders them. */
export const SETTINGS_GROUPS = ["You", "Workspace", "Advanced"] as const;
export type SettingsGroup = (typeof SETTINGS_GROUPS)[number];

export interface SettingsNavItem {
  to: string;
  label: string;
  /** Sub-title under the page heading — says what the page decides, in one line. */
  description: string;
  group: SettingsGroup;
  /** Match the path exactly (vs prefix), so the index isn't active under children. */
  exact?: boolean;
  /** RBAC resource gating VISIBILITY of the entry (always visible when omitted). */
  resource?: string;
  /** RBAC action; defaults to `list`. */
  action?: string;
  /**
   * Plan entitlement this page is PACKAGED behind — the other question from
   * `resource`. That one an administrator in the customer's own org can grant;
   * this one only a subscription change can. Same field, same meaning and same
   * default (omitted → part of the product) as `NavItem.capability`.
   */
  capability?: string;
}

export const SETTINGS_NAV: readonly SettingsNavItem[] = [
  {
    to: ROUTES.settings,
    label: "General",
    description: "How the dashboard looks and behaves for you, in this browser.",
    group: "You",
    exact: true,
  },
  {
    to: ROUTES.settingsAccess,
    label: "Access",
    description:
      "What your session is allowed to do. This is why a control elsewhere is offered or disabled.",
    group: "You",
  },
  // Deliberately UNGATED, as it has been since it was a tab. Anyone who can
  // export can read their own record of having done so — hiding a person's own
  // audit trail behind a permission is the one gate with no reader. The
  // tenancy-wide lane INSIDE the page is restricted, and by the backend, which
  // is where the caller's tier is actually known.
  {
    to: ROUTES.settingsExports,
    label: "Exports",
    description: "Every dataset taken out of the product, and by whom.",
    group: "You",
  },
  // The one page in Settings that is genuinely about being MULTI-tenant. A
  // customer with a single tenant has nothing to do here, which is why it is
  // the natural first candidate for packaging — every tier grants it today.
  // A personal access token is a "You" setting even though the page also lists
  // the workspace's shared keys: the common case is minting one that acts as
  // you, and every member can do it. Gated on the READ verb — the page's first
  // act is a listing, so gating on `create` would open a page whose table 403s.
  {
    to: ROUTES.settingsApiKeys,
    capability: ENTITLEMENT.API_KEYS,
    label: "API keys",
    description:
      "Credentials that call the platform as you, for scripts and services. Shown once, revocable any time.",
    group: "You",
    resource: RESOURCES.apiKeys,
    action: "show",
  },
  {
    to: ROUTES.settingsWorkspace,
    label: "Organizations & tenants",
    description:
      "Rename the workspace, switch tenant, and manage the tenants under your organization.",
    group: "Workspace",
    capability: ENTITLEMENT.TENANCY_SWITCHING,
  },
  // Gated on the members listing it reads (`users:list`, every role's): a
  // member sees the orgs they belong to, and the page says what it withholds.
  {
    to: ROUTES.settingsStructure,
    capability: ENTITLEMENT.TENANCY_SWITCHING,
    label: "Structure",
    description: "Your organizations, tenants and teams, and where each person holds a role.",
    group: "Workspace",
    resource: RESOURCES.team,
    action: "list",
  },
  {
    to: ROUTES.settingsDefaults,
    capability: ENTITLEMENT.WORKSPACE_DEFAULTS,
    label: "Defaults",
    description:
      "Who can see what you create, and which saved view opens by default — for you, or for everyone.",
    group: "Workspace",
  },
  {
    to: ROUTES.settingsBilling,
    label: "Billing",
    description: "Your plan, what it includes, and what this workspace has used.",
    group: "Workspace",
    resource: RESOURCES.subscriptions,
  },
  // Configuration starts on Pro; automatic transport still works on Free.
  {
    to: ROUTES.settingsApis,
    capability: ENTITLEMENT.GRAPHQL_PER_OPERATION,
    label: "APIs",
    description: "Which transport this account's reads use — REST, or the GraphQL fast path.",
    group: "Advanced",
  },
];

/**
 * Everything is included when no plan predicate is supplied.
 *
 * A caller with no plan context — a test, the first frames before the
 * subscription read lands — sees the rail it saw before packaging existed.
 * The inverse default would hide paid pages from paying customers on every
 * page load, which is the one failure mode a ceiling must never have.
 */
const ENTITLED_TO_EVERYTHING = (): boolean => true;

/**
 * The entries this session may see, in table order.
 *
 * Takes BOTH gates, like the rail's `visibleNavItems`: permission answers "may
 * this person", entitlement answers "does this customer's plan include it". A
 * rail filtered on one of them shows a page the other refuses.
 */
export function visibleSettingsNav(
  can: (resource: string, action: string) => boolean,
  entitled: (capability: string | undefined) => boolean = ENTITLED_TO_EVERYTHING,
  items: readonly SettingsNavItem[] = SETTINGS_NAV,
  sharedLadder = true,
): SettingsNavItem[] {
  return items.filter(
    (item) =>
      (!item.resource || can(item.resource, item.action ?? "list")) &&
      (sharedLadder || item.capability !== ENTITLEMENT.TENANCY_SWITCHING) &&
      // Symmetric with the resource gate: an entry that is not packaged is never
      // put to the predicate, so no predicate can hide the product itself.
      (!item.capability || entitled(item.capability)),
  );
}

/**
 * The entry a pathname is sitting on — longest match wins, so `/settings/access`
 * resolves to Access rather than to the `exact`-matched index.
 *
 * Used for the page heading, so a route with no entry (or one the caller may not
 * see) yields `undefined` and the heading falls back to "Settings" rather than
 * claiming a page the reader is not on.
 */
export function activeSettingsItem(
  pathname: string,
  items: readonly SettingsNavItem[] = SETTINGS_NAV,
): SettingsNavItem | undefined {
  let best: SettingsNavItem | undefined;
  for (const item of items) {
    const hit = item.exact ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`);
    if (hit && (!best || item.to.length > best.to.length)) best = item;
  }
  return best;
}
